/**
 * TestedAssetSlider — the report hero's picture card: what was tested.
 *
 * One slide per tested asset that has a picture, in item-letter order (the
 * dialog's order and the letters on every chart), each in the same frame the
 * leaderboard uses: a creative whole on a blurred copy of itself, a landing
 * page's top, a video's poster with the play glyph. The pictures are the
 * analysis's (`assetPreviews`, Python's one resolver); the report's own survey
 * cannot see a video's poster.
 *
 * Two or more slides get left/right buttons, dots and the arrow keys, stepping
 * with wrap-around; there is no autoplay and no transition, so reduced motion
 * needs nothing extra. One slide shows alone, with no controls. A click opens
 * the existing asset dialog on that asset.
 */
import { KeyboardEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ArrowRight } from '@digital-twins/design-system/icons'

import { cn } from '@/lib/utils'
import { useContentLanguage } from '@/i18n/contentLanguageContext'
import { assetCountLabelKey, assetLabel, PreviewedAsset } from '@/features/studies/report/utils/testedAssets'
import { AssetFrame } from '@digital-twins/deck-ui/components'

import { TestedAsset, respondentWordingFor } from '@digital-twins/shared-types'

export interface TestedAssetSliderProps {
  /** Every tested asset: names the carousel ("2 ads"). */
  assets: Array<TestedAsset>
  /** The slides: the previewed assets in letter order (`previewedAssets`), at least one. */
  slides: Array<PreviewedAsset>
  itemTags: Record<string, string>
  onOpen: (assetId: string) => void
  className?: string
}

const CONTROL =
  'flex h-8 w-8 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm transition-colors hover:bg-black/65 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white'

export function TestedAssetSlider(props: TestedAssetSliderProps) {
  const { assets, slides, itemTags, onOpen, className } = props

  const [index, setIndex] = useState(0)

  const { t } = useTranslation('app')
  // A price reads in the study's content language, as the dialog names it.
  const priceWording = respondentWordingFor(useContentLanguage())

  const count = slides.length
  const position = Math.min(index, count - 1)
  const { asset, preview } = slides[position]
  const name = assetLabel(asset, priceWording)
  const tag = itemTags[asset.id]
  const isCarousel = count > 1

  const step = (direction: -1 | 1) => setIndex((position + direction + count) % count)

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!isCarousel || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) {
      return
    }
    event.preventDefault()
    step(event.key === 'ArrowLeft' ? -1 : 1)
  }

  return (
    <section
      data-testid="report-hero-asset-slider"
      aria-roledescription={isCarousel ? 'carousel' : undefined}
      aria-label={t(assetCountLabelKey(assets), { count: assets.length })}
      onKeyDown={onKeyDown}
      className={cn('relative overflow-hidden rounded-2xl bg-muted', className)}
    >
      <div aria-live="polite" className="absolute inset-0">
        <div
          role="group"
          aria-roledescription="slide"
          aria-label={isCarousel ? t('studyReport.assetSlide', { position: position + 1, count, name }) : name}
          data-testid="report-hero-asset-slide"
          data-slide-index={position}
          className="absolute inset-0"
        >
          <button
            type="button"
            aria-label={t('studyReport.assetOpen', { name })}
            onClick={() => onOpen(asset.id)}
            className="absolute inset-0 block h-full w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            <AssetFrame
              key={asset.id}
              url={preview.url}
              kind={preview.kind}
              alt={asset.media.find((media) => media.altText)?.altText || name}
              radius={0}
              playSize={48}
              loading="eager"
              data-testid="report-hero-asset-frame"
              style={{ width: '100%', height: '100%' }}
            />
          </button>
          <div
            className={cn(
              'pointer-events-none absolute inset-x-0 bottom-0 flex items-end bg-gradient-to-t from-black/70 via-black/25 to-transparent px-4 pb-3 pt-10',
              isCarousel && 'pr-24',
            )}
          >
            <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-white" data-testid="report-hero-asset-caption">
              {tag && (
                <span className="inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-md bg-white px-1.5 text-xs font-bold text-black">
                  {tag}
                </span>
              )}
              <span className="truncate">{name}</span>
            </span>
          </div>
        </div>
      </div>

      {isCarousel && (
        <>
          <button
            type="button"
            data-testid="report-hero-asset-prev"
            aria-label={t('studyReport.assetPrev')}
            onClick={() => step(-1)}
            className={cn(CONTROL, 'absolute left-3 top-1/2 -translate-y-1/2')}
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.8} />
          </button>
          <button
            type="button"
            data-testid="report-hero-asset-next"
            aria-label={t('studyReport.assetNext')}
            onClick={() => step(1)}
            className={cn(CONTROL, 'absolute right-3 top-1/2 -translate-y-1/2')}
          >
            <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
          </button>
          <div className="absolute bottom-4 right-4 flex items-center gap-1.5" data-testid="report-hero-asset-dots">
            {slides.map((slide, dot) => (
              <button
                key={slide.asset.id}
                type="button"
                data-testid={`report-hero-asset-dot-${dot}`}
                aria-label={t('studyReport.assetGoTo', { name: assetLabel(slide.asset, priceWording) })}
                aria-current={dot === position ? 'true' : undefined}
                onClick={() => setIndex(dot)}
                className={cn(
                  'rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white',
                  dot === position ? 'h-1.5 w-5 bg-white' : 'h-1.5 w-1.5 bg-white/50 hover:bg-white/80',
                )}
              />
            ))}
          </div>
        </>
      )}
    </section>
  )
}
